import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPickupLocations } from '../feature';
import { useAppDispatch } from '@/store';
import debounce from 'lodash.debounce';
import { DEFAULT_LOCALE } from '@/models/localization.model';
import { IPickup } from '@/models/regions.model';

export const useAutocompleteWarehouses = (selectedSettlementId?: string | null) => {
  const [isLoading, setIsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<IPickup[]>([]);
  const dispatch = useAppDispatch();

  const handleSearch = useCallback(
    debounce(async (search: string, settlementId?: string) => {
      if (!settlementId) {
        setWarehouses([]);
        return;
      }

      try {
        setIsLoading(true);
        const response = await dispatch(
          getPickupLocations({
            address: search,
            settlement_id: settlementId,
            is_active: '1',
          }),
        ).unwrap();

        setWarehouses(response.data ?? []);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [dispatch],
  );

  useEffect(() => {
    setWarehouses([]);

    if (!selectedSettlementId) {
      return;
    }

    handleSearch('', selectedSettlementId);
  }, [handleSearch, selectedSettlementId]);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  return useMemo(
    () => ({
      warehouses: selectedSettlementId ? warehouses : [],
      warehousesOptions: selectedSettlementId
        ? warehouses.map((item) => ({
            label: item.locale?.[DEFAULT_LOCALE]?.address || item.locale?.[DEFAULT_LOCALE]?.name || '',
            value: item.id,
          }))
        : [],
      isLoading,
      onSearch: handleSearch,
    }),
    [warehouses, isLoading, handleSearch, selectedSettlementId],
  );
};
